import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { Category } from "./BudgetTable";

interface Props {
  categories: Category[];
}

export default function BudgetChart({ categories }: Props) {
  const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);
  // Aggregate by top-level category and then stack them in the chart
  const topLevel = categories.map((cat) => {
    const aggregate = (c: Category, parentKind?: "income" | "expense") => {
      let budgeted = 0;
      let actual = 0;

      const walk = (node: Category, inheritedKind?: "income" | "expense") => {
        const inferredKind: "income" | "expense" =
          node.kind ??
          inheritedKind ??
          (node.name.toLowerCase().includes("income") || node.id.includes("income")
            ? "income"
            : "expense");

        const hasSubs = !!node.subcategories?.length;

        if (hasSubs && node.subcategories) {
          node.subcategories.forEach((sub) => walk(sub, inferredKind));
        }

        if (!hasSubs) {
          const intervalMonths =
            node.billingIntervalMonths && node.billingIntervalMonths > 0
              ? node.billingIntervalMonths
              : 1;
          const monthlyActual = node.actual / intervalMonths;

          // Use absolute magnitudes for stacking; sign semantics are in legend/naming
          const b = Math.abs(node.budgeted);
          const a = Math.abs(monthlyActual);

          budgeted += b;
          actual += a;
        }
      };

      walk(c, parentKind);
      return { budgeted, actual };
    };

    const { budgeted, actual } = aggregate(cat, cat.kind);
    return {
      id: cat.id,
      name: cat.name,
      kind:
        cat.kind ??
        (cat.name.toLowerCase().includes("income") || cat.id.includes("income")
          ? "income"
          : "expense"),
      budgeted,
      actual,
    };
  });

  // Build two X-axis groups (Income, Expenses) and, within each,
  // show two stacked bars side by side: Budgeted and Actual.
  const data = ["Income", "Expenses"].map((label) => {
    const row: any = { label };
    topLevel.forEach((cat) => {
      const isIncome = cat.kind === "income";
      const matchesLabel =
        (label === "Income" && isIncome) || (label === "Expenses" && !isIncome);

      const budgetKey = `${cat.id}_budgeted`;
      const actualKey = `${cat.id}_actual`;

      row[budgetKey] = matchesLabel ? cat.budgeted : null;
      row[actualKey] = matchesLabel ? cat.actual : null;
    });
    return row;
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Math.abs(value));

  const renderTooltip = (tooltipProps: any) => {
    const { active, payload, activePayload } = tooltipProps;
    const list = (activePayload && activePayload.length
      ? activePayload
      : payload) as any[] | undefined;

    if (!active || !hoveredKey || !list || !list.length) return null;

    const source = list.find((p) => p.dataKey === hoveredKey);

    if (!source) return null;

    const key: string = source.dataKey || "";
    const baseId = key.replace(/_(budgeted|actual)$/ , "");
    const cat = topLevel.find((c) => c.id === baseId);
    const isBudgeted = key.endsWith("_budgeted");
    const label = cat ? `${cat.name} (${isBudgeted ? "Budgeted" : "Actual"})` : key;
    const value = typeof source.value === "number" ? source.value : 0;

    return (
      <div
        style={{
          backgroundColor: "var(--template-palette-background-paper, #fff)",
          color: "var(--template-palette-text-primary, #000)",
          borderRadius: 8,
          padding: "6px 10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ fontSize: 12, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{formatCurrency(value)}</div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart
        data={data}
        margin={{ top: 16, right: 24, left: 0, bottom: 8 }}
        barCategoryGap="40%"
      >
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
        <XAxis
          dataKey="label"
          tickFormatter={(value: string) =>
            value === "Income"
              ? "Income (Budgeted / Actual)"
              : "Expenses (Budgeted / Actual)"
          }
        />
        <YAxis tickFormatter={(v) => formatCurrency(v)} />
        <Tooltip
          content={renderTooltip}
          cursor={false}
          wrapperStyle={{ transition: "none" }}
        />
        <Legend
          content={() => (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {topLevel.map((cat, index) => {
                const color =
                  cat.kind === "income"
                    ? "#4caf50"
                    : ["#2196f3", "#ff9800", "#9c27b0", "#f44336"][index % 4];
                return (
                  <div
                    key={cat.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginRight: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 2,
                        backgroundColor: color,
                        marginRight: 4,
                      }}
                    />
                    <span style={{ fontSize: 12 }}>{cat.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        />
        {topLevel.map((cat, index) => {
          const color =
            cat.kind === "income"
              ? "#4caf50"
              : ["#2196f3", "#ff9800", "#9c27b0", "#f44336"][index % 4];
          const budgetKey = `${cat.id}_budgeted`;
          const actualKey = `${cat.id}_actual`;

          return (
            <React.Fragment key={cat.id}>
              <Bar
                dataKey={budgetKey}
                stackId="budgeted"
                fill={color}
                barSize={24}
                radius={[0, 0, 0, 0]}
                onMouseOver={() => setHoveredKey(budgetKey)}
                onMouseLeave={() => setHoveredKey(null)}
              />
              <Bar
                dataKey={actualKey}
                stackId="actual"
                fill={color}
                barSize={24}
                radius={[0, 0, 0, 0]}
                onMouseOver={() => setHoveredKey(actualKey)}
                onMouseLeave={() => setHoveredKey(null)}
              />
            </React.Fragment>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
}
